import { useQuery } from '@tanstack/react-query';
import isEmpty from 'lodash/isEmpty';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { FieldPath, useFormContext } from 'react-hook-form';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import { DocumentsData } from '@/features/ui/DocumentsData';
import { FormDocument } from '@/features/ui/DocumentsData/model/types';

import { RMPServiceCode } from '@/shared/types/serviceCodes';
import { ConditionView, FormSelect, FormSwitch, FormTextInput, Grid, StepControlPanel } from '@/shared/ui';
import { MultipleFormInputPhone } from '@/shared/ui/InputPhone/MultipleFormInputPhone';
import { FormTextArea } from '@/shared/ui/TextArea/FormTextArea';

import { FormConstants, genders } from '@/components/Forms/constants';

import AuthContext from '@/contexts/Auth/AuthContext';

import { rmp } from '@/api/integrations/rmp';
import { queryKeys } from '@/api/queryKeys';
import { DocumentTypeEntity } from '@/api/types';

import api from '@/api';

import { missingPersonExtractAllowedFileTypes, OTHER_ANCESTRAL_CONNECTION_ID } from '../../lib/constants';
import { ApplicantInfoSchema } from '../../model/types';

const deleteApplicantDraftAttachments = async (requestId: string): Promise<void> => {
  const { data: draft } = await api.services.draft.getDetails(requestId);
  const ids = Object.entries(draft.attachments ?? {})
    .filter(([field]) => field.startsWith('applicant.'))
    .flatMap(([, files]) => files.map(({ uuid }) => uuid));

  if (ids.length) {
    await api.services.deleteAttachments({ request_id: requestId, ids });
  }
};

export const ApplicantInfo: React.FC<StepProps> = ({ next, prev, serviceCode, currentRequestId, onStepDraftSave }) => {
  const { control, setValue, getValues, watch, formState, register } = useFormContext<ApplicantInfoSchema>();
  const { userData } = useContext(AuthContext);

  const ancestralConnection = watch('applicant.ancestralConnection');
  const prevConnectionIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    prevConnectionIdRef.current = ancestralConnection?.id;
  }, [ancestralConnection?.id]);

  const handleAncestralConnectionChange = (): void => {
    const nextId = getValues('applicant.ancestralConnection.id');
    const prevId = prevConnectionIdRef.current;

    if (!prevId || !nextId || !currentRequestId || prevId === nextId) return;

    (getValues('applicant.documents') ?? []).forEach((_: unknown, index: number) => {
      setValue(`applicant.documents.${index}.0.attachments`, {});
    });
    setValue('applicant.additional_document.0.attachments', {});

    void deleteApplicantDraftAttachments(currentRequestId).catch(console.error);
  };

  const { data: optionsFamilyRelationshipDegrees = [], isLoading: isFamilyRelationshipDegreesLoading } = useQuery({
    queryKey: queryKeys.rmp.familyRelationshipDegrees(serviceCode as string),
    queryFn: () =>
      rmp.dictionary.getFamilyRelationshipDegrees(serviceCode as RMPServiceCode.MISSING_PERSON_EXTRACT).then((r) =>
        r.data.map((d) => ({
          id: d.id,
          label: d.name,
          required_documents: d.required_documents.map((rd) => ({
            id: rd.id,
            label: rd.name,
            code: rd.id as DocumentTypeEntity['code'],
          })),
        })),
      ),
    enabled: !!serviceCode,
  });

  const requiredDocuments = useMemo(
    () => optionsFamilyRelationshipDegrees.find((option) => option.id === ancestralConnection?.id)?.required_documents,
    [ancestralConnection, optionsFamilyRelationshipDegrees],
  );

  useEffect(() => {
    // prefill form data
    const formData = getValues();

    isEmpty(formData.applicant?.additional_document?.[0]?.type) &&
      setValue('applicant.additional_document.0.type', {
        id: '09',
        label: 'Інше',
        code: '09' as DocumentTypeEntity['code'],
      });
    isEmpty(formData.applicant?.firstName) && setValue('applicant.firstName', userData?.name_first ?? '');
    isEmpty(formData.applicant?.lastName) && setValue('applicant.lastName', userData?.name_last ?? '');
    isEmpty(formData.applicant?.middleName) &&
      userData?.name_middle &&
      setValue('applicant.middleName', userData?.name_middle);
    isEmpty(formData.applicant?.email) &&
      userData?.contact_email &&
      setValue('applicant.email', userData?.contact_email);
    isEmpty(formData.applicant?.phones) && setValue('applicant.phones', [userData?.contact_phone ?? '']);
    isEmpty(formData.applicant?.rnokpp) && setValue('applicant.rnokpp', userData?.drfo_code ?? '');
    isEmpty(formData.applicant?.gender) &&
      userData?.gender !== 'U' &&
      setValue(
        'applicant.gender',
        genders.find((gender) => gender.id === userData?.gender) as { id: string; label: string },
      );
    isEmpty(formData.applicant?.documents) && setValue('applicant.documents', []);
  }, []);

  requiredDocuments?.forEach((_, index) => {
    register(`applicant.documents.${index}` as FieldPath<ApplicantInfoSchema>);
  });
  if (ancestralConnection && ancestralConnection.id !== OTHER_ANCESTRAL_CONNECTION_ID) {
    register('applicant.additional_document' as FieldPath<ApplicantInfoSchema>);
  }

  return (
    <ConditionView if={!isFamilyRelationshipDegreesLoading} else={null}>
      <FormBlock title="Особисті дані заявника">
        <FormTextInput
          name="applicant.lastName"
          control={control}
          label="Прізвище"
          placeholder="Вкажіть прізвище"
          maxLength={FormConstants.INPUT_MAX_LENGTH_255 + 1}
          required
          disabled
        />
        <FormTextInput
          name="applicant.firstName"
          control={control}
          label="Імʼя"
          placeholder="Вкажіть імʼя"
          maxLength={FormConstants.INPUT_MAX_LENGTH_255 + 1}
          required
          disabled
        />
        <FormTextInput
          name="applicant.middleName"
          control={control}
          maxLength={FormConstants.INPUT_MAX_LENGTH_255 + 1}
          label="По батькові"
          placeholder="Вкажіть по батькові"
          disabled
        />
        <FormSelect
          name="applicant.gender"
          control={control}
          label="Стать"
          options={genders.filter((gender) => gender.id !== 'U')}
          required
        />
        <FormTextInput name="applicant.email" placeholder="example@example.com" control={control} label="Email" />
        <MultipleFormInputPhone
          onChange={(phones) => phones && setValue('applicant.phones', phones)}
          maxPhoneNumbersQuantity={4}
          value={getValues()?.applicant?.phones}
          errors={formState.errors.applicant?.phones as Array<{ message: string }>}
          required
        />
        <FormTextInput
          name="applicant.rnokpp"
          control={control}
          label="Ідентифікаційний код (РНОКПП)"
          maxLength={FormConstants.INPUT_MAX_LENGTH_10}
          disabled
          required
        />
        <FormSwitch
          name="applicant.resubmittingRequest"
          control={control}
          label="Повторне подання запиту на отримання витягу"
        />
      </FormBlock>

      <FormBlock title="Родинні звʼязки">
        <FormSelect
          name="applicant.ancestralConnection"
          label="Ким ви приходитесь зниклій особі"
          control={control}
          options={optionsFamilyRelationshipDegrees}
          onChange={handleAncestralConnectionChange}
          required
        />
        {ancestralConnection && (
          <>
            {requiredDocuments?.map((requiredDocument, index) => (
              <FormBlock key={`${ancestralConnection.id}_${requiredDocument.id}`}>
                <DocumentsData
                  withUploader
                  documentTypeSelectDisabled
                  documentsRequired
                  name={`applicant.documents.${index}`}
                  documentUploadLabel={requiredDocument.label ?? ''}
                  allowedFileTypes={missingPersonExtractAllowedFileTypes}
                  documentTypeEntities={requiredDocument ? [requiredDocument] : []}
                  maxFileCount={5}
                  maxDocumentsCount={1}
                  request_id={currentRequestId ?? ''}
                  onSaveDraft={onStepDraftSave}
                />
              </FormBlock>
            ))}
            {ancestralConnection.id !== OTHER_ANCESTRAL_CONNECTION_ID ? (
              <DocumentsData
                withUploader
                documentTypeSelectDisabled
                documentsRequired={false}
                fileRequired={false}
                key={ancestralConnection.id}
                name="applicant.additional_document"
                documentUploadLabel="Завантажте документ"
                allowedFileTypes={missingPersonExtractAllowedFileTypes}
                documentTypeEntities={[{ id: '09', label: 'Інше', code: '09' as DocumentTypeEntity['code'] }]}
                maxFileCount={5}
                maxDocumentsCount={1}
                request_id={currentRequestId ?? ''}
                onSaveDraft={onStepDraftSave}
              />
            ) : (
              <></>
            )}
            <FormTextArea
              name="applicant.additional_info"
              control={control}
              label="Додаткові відомості"
              maxLength={FormConstants.INPUT_MAX_LENGTH_255 + 1}
            />
          </>
        )}
      </FormBlock>

      <StepControlPanel prev={prev} next={next} />
    </ConditionView>
  );
};

export const ApplicantInfoPreview: React.FC = () => {
  const { getValues } = useFormContext<ApplicantInfoSchema>();
  const { applicant } = getValues();

  return (
    <Grid
      title="Особисті дані заявника"
      data={[
        ['Прізвище', applicant?.lastName],
        ['Імʼя', applicant?.firstName],
        ['По батькові', applicant?.middleName],
        ['Стать', applicant?.gender?.label],
        ['Email', applicant?.email],
        ['Номер телефона', applicant?.phones?.join(', ')],
        ['Ідентифікаційний код (РНОКПП)', applicant?.rnokpp],
      ]}
    />
  );
};

export const AncestralConnectionPreview: React.FC = () => {
  const { getValues } = useFormContext<ApplicantInfoSchema>();
  const { applicant } = getValues();

  return (
    <Grid
      title="Родинні звʼязки"
      data={[
        ['Ким ви приходитесь зниклій особі', applicant?.ancestralConnection.label],
        ...(applicant?.documents ?? []).map((document: Nullable<Array<Partial<FormDocument>>>) => {
          const attachment = document?.[0]?.attachments;

          return [
            document?.[0]?.type?.label,
            attachment?.fileNames
              ? Object.values(attachment.fileNames).join(', ')
              : (attachment?.file_name ?? attachment?.fileName ?? null),
          ] as [string, Nullable<string>];
        }),
        ...(applicant?.ancestralConnection?.id !== OTHER_ANCESTRAL_CONNECTION_ID &&
        applicant?.additional_document?.[0]?.attachments?.length
          ? [
              [
                'Додаткові документи',
                applicant.additional_document[0].attachments.fileNames
                  ? Object.values(applicant.additional_document[0].attachments.fileNames).join(', ')
                  : (applicant?.additional_document?.[0]?.attachments?.file_name ??
                    applicant?.additional_document?.[0]?.attachments?.fileName ??
                    null),
              ],
            ]
          : []),
        ...(applicant?.additional_info ? [['Додаткові відомості', applicant.additional_info]] : []),
      ]}
    />
  );
};
